import useAgency from 'features/Agency/useAgency';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown, { KTBSDropdownDirections } from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ChargeStationCompactListFilterView from './components/ChargeStationCompactListFilterView';
import MenuActionDropdown from 'general/components/Others/MenuActionDropdown';
import useAppConfig from 'hooks/useAppConfig';

ChargeStationCompactListView.propTypes = {
  prefKey: PropTypes.string,
  hasAppTab: PropTypes.bool,
  initialFilter: PropTypes.object,
};

function ChargeStationCompactListView({ prefKey, hasAppTab, initialFilter }) {
  // MARK: ---- PROPERTIES ----
  const appConfig = useAppConfig();
  const omitColumnIds = useMemo(() => {
    if (Utils.isValid(initialFilter?.agencyId)) {
      return ['MANAGEMENT_OBJECT', 'AGENCY'];
    }
    return [];
  }, [initialFilter]);
  const defaultColumnIds = useMemo(() => {
    return [
      'CODE',
      'NAME',
      'OPERATOR',
      'MANAGEMENT_OBJECT',
      'AGENCY',
      'VEHICLE_TYPE',
      'AVAILABLE_POINT_COUNT',
      'POINT_TYPE',
      'STATUS',
      'TOTAL_TRANSACTION_COUNT',
      'TOTAL_ENERGY_SELL',
      'ADDRESS',
      'CREATED_AT',
    ].filter((id) => !omitColumnIds.includes(id));
  }, [omitColumnIds]);
  const router = useRouter();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const [loading, setLoading] = useState(false);
  const [stationList, setStationList] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({
    page: 0,
    limit: AppConfigs.defaultPageLimit,
    status: '',
    operatorId: '',
    q: '',
    agencyId: initialFilter?.agencyId ?? '',
    landlordId: initialFilter?.landlordId ?? '',
    areaId: '',
    vehicleType: '',
  });
  const areaTree = useSelector((state) => state.app.areaTree);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'CODE',
        name: UIHelper.buildTableColumn('Mã trạm sạc'),
        selector: (row) => row?.stationCode,
        width: '150px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'NAME',
        name: UIHelper.buildTableColumn('Tên trạm sạc'),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.name}>
            <span>{row?.name}</span>
          </KTTooltip>
        ),
        width: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OPERATOR',
        name: UIHelper.buildTableColumn('Nhà điều hành'),
        selector: (row) => row?.chargeOperator?.name,
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MANAGEMENT_OBJECT',
        name: UIHelper.buildTableColumn('Đối tượng quản lý'),
        selector: (row) =>
          Utils.isValid(row?.agencyId)
            ? Utils.isValid(row?.landlordId)
              ? 'Dự án HTKD'
              : row?.agencyId === appConfig?.groupAgencyId
                ? 'Dự án HTKD'
                : 'NĐT nhượng quyền'
            : 'Nhà điều hành',
        width: '180px',
        style: tableStyle.columns.normal,
      },
      // {
      //   id: 'AGENCY',
      //   name: UIHelper.buildTableColumn('NĐT/Chủ mặt bằng'),
      //   omit: !isAdmin,
      //   selector: (row) => (
      //     <span
      //       className="cursor-pointer hover-opacity-60"
      //       onClick={() => {
      //         if (Utils.isValid(row?.agencyId) && !Utils.isValid(row?.landlordId) && row?.agencyId !== appConfig?.groupAgencyId) {
      //           Utils.openInNewTab(`/investor/${row?.agencyId}`);
      //         } else if (Utils.isValid(row?.landlordId)) {
      //           Utils.openInNewTab(`/landlord/${row?.landlordId}`);
      //         }
      //       }}
      //     >
      //       <KTTooltip offset={[0, 0]}
      //         text={
      //           Utils.isValid(row?.agencyId) && !Utils.isValid(row?.landlordId) && row?.agencyId !== appConfig?.groupAgencyId
      //             ? row?.investor?.name
      //             : row?.landlord?.name
      //         }>
      //         <span>
      //           {Utils.isValid(row?.agencyId) && !Utils.isValid(row?.landlordId) && row?.agencyId !== appConfig?.groupAgencyId
      //             ? row?.investor?.name
      //             : row?.landlord?.name
      //           }
      //         </span>
      //       </KTTooltip>
      //     </span>
      //   ),
      //   width: '240px',
      //   style: tableStyle.columns.link,
      // },
      {
        id: 'VEHICLE_TYPE',
        name: UIHelper.buildTableColumn('Loại phương tiện'),
        selector: (row) =>
          row?.vehicleTypes?.length
            ? _.find(
              ChargeHelper.Data.vehicleTypeOptions,
              (item) => item.value === row?.vehicleTypes[0]
            )?.name
            : '',
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'AVAILABLE_POINT_COUNT',
        name: UIHelper.buildTableColumn('Trụ sạc còn trống'),
        selector: (row) =>
          `${ChargeHelper.Utils.getChargeStationAvailablePoints(row?.chargePoints)}/${row?.chargePoints?.length}`,
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'POINT_TYPE',
        name: UIHelper.buildTableColumn('Loại trụ sạc'),
        selector: (row) => ChargeHelper.Utils.getChargeStationPointTypes(row),
        width: '100px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái trạm sạc'),
        selector: (row) => (
          <KTLabel
            text={ChargeHelper.Utils.getChargeStationStatusName(row?.status)}
            isInline={true}
            size={KTLabelSize.medium}
            additionalClassName={`label-light-${ChargeHelper.Utils.getChargeStationStatusColor(
              row?.status
            )}`}
          />
        ),
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'TOTAL_TRANSACTION_COUNT',
        name: UIHelper.buildTableColumn('Tổng số phiên sạc'),
        selector: (row) => '',
        width: '140px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'TOTAL_ENERGY_SELL',
        name: UIHelper.buildTableColumn('Số điện bán được', 'kWh'),
        selector: (row) => '',
        width: '140px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'ADDRESS',
        name: UIHelper.buildTableColumn('Địa chỉ'),
        selector: (row) => {
          const address = ChargeHelper.Utils.getChargeStationFullAddress(row);
          return (
            <KTTooltip offset={[0, 0]} text={address}>
              <span>{address}</span>
            </KTTooltip>
          );
        },
        minWidth: '260px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CREATED_AT',
        name: UIHelper.buildTableColumn('Ngày tạo'),
        selector: (row) => Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY'),
        width: '120px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        maxWidth: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        center: false,
        cell: (row) => {
          const actions = buildMoreActions(row);
          return !_.isEmpty(actions) && UIHelper.buildMoreActionsButton(actions, true);
        },
      },
    ];
  }, [areaTree, isAdmin]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
  useEffect(() => {
    const params = _.pickBy(
      filter,
      (value) => value !== null && value !== undefined && value !== ''
    );
    ChargeHelper.API.requestGetChargeStationList(params).then((data) => {
      setStationList(data?.chargeStations);
      setTotal(data?.total);
    });
  }, [filter]);
  const { operatorList, operatorOptions } = useAgency();
  const windowSize = useWindowSize();

  // MARK: ---- METHODS ----
  const handleRefresh = () => {
    setFilter({ ...filter });
  };

  const buildMoreActions = (chargeStationDetail) => {
    const actions = [];
    // xem chi tiet
    actions.push({
      text: 'Xem chi tiết',
      iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
      click: () => {
        router.navigate(`${AppRoute.stationList}/${chargeStationDetail?.stationId}`);
      },
    });
    // chinh sua
    if (isAdmin) {
      actions.push({
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          router.navigate(`${AppRoute.stationList}/${chargeStationDetail?.stationId}/edit`);
        },
      });
    }
    if (isAdmin &&
      [ChargeConstants.ChargeStationStatus.NORMAL].includes(chargeStationDetail?.status)
    )
      actions.push({
        text: 'Ngừng hoạt động',
        iconElement: <i className="fa-regular fa-stop" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmStopChargeStation(chargeStationDetail, () => {
            handleRefresh();
          });
        },
      });
    if (isAdmin &&
      [ChargeConstants.ChargeStationStatus.NORMAL].includes(chargeStationDetail?.status)
    )
      actions.push({
        text: 'Ẩn trạm sạc',
        iconElement: <i className="fa-regular fa-store-lock" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmHideChargeStation(chargeStationDetail, () => {
            handleRefresh();
          });
        },
      });
    if (isAdmin &&
      [
        ChargeConstants.ChargeStationStatus.STOPPED,
        ChargeConstants.ChargeStationStatus.HIDDEN,
        ChargeConstants.ChargeStationStatus.MAINTAINING,
      ].includes(chargeStationDetail?.status)
    )
      actions.push({
        text: 'Khôi phục hoạt động',
        iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmRestoreChargeStation(chargeStationDetail, () => {
            handleRefresh();
          });
        },
      });
    if (isAdmin)
      actions.push({
        text: 'Xoá',
        iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmDeleteChargeStation(chargeStationDetail, () => {
            handleRefresh();
          });
        },
      });
    return actions;
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div className={`pb-4 ${hasAppTab ? 'pt-4' : ''}`}>
          {/* header */}
          {!hasAppTab && (
            <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
              <div className="d-flex flex-column">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Danh sách trạm sạc
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                    {Utils.formatNumber(total)} trạm sạc
                  </span>
                </span>
              </div>
              <div>
                {isAdmin &&
                  UIHelper.buildGreenButton(
                    'Thêm trạm sạc',
                    () => {
                      router.navigate('add');
                    },
                    <i className="fa-regular fa-circle-plus" />
                  )}
              </div>
            </div>
          )}

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              {windowSize.width >= 768 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Tìm kiếm')}
                  <KTFormSearch
                    name="search"
                    placeholder="Theo mã trạm sạc, tên trạm sạc, địa chỉ..."
                    value={filter.q}
                    onSubmit={(text) => {
                      setFilter({ ...filter, q: text, page: 0 });
                    }}
                  />
                </div>
              )}
              {/* loc theo nha dieu hanh */}
              {windowSize.width >= 1120 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Nhà điều hành')}
                  <KTBSDropdown
                    name="operator"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(operatorList, (item) => item?.operatorId === filter?.operatorId)
                          ?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={filter?.operatorId}
                    dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...operatorOptions]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      setFilter({ ...filter, operatorId: value, page: 0 });
                    }}
                    isDropdownForm={true}
                    direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                  />
                </div>
              )}
              {/* loc theo loai phuong tien */}
              {windowSize.width >= 1368 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Loại phương tiện')}
                  <KTBSDropdown
                    name="operator"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(
                          ChargeHelper.Data.vehicleTypeOptions,
                          (item) =>
                            item?.value === filter?.vehicleType
                        )?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={filter?.vehicleType}
                    dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...ChargeHelper.Data.vehicleTypeOptions]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      setFilter({ ...filter, vehicleType: value, page: 0 });
                    }}
                    isDropdownForm={true}
                    direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                  />
                </div>
              )}
              {/* loc theo trang thai */}
              {windowSize.width >= 1368 && (
                <></>
                // <div className="col mb-4 px-2">
                //   {UIHelper.buildFormLabel('Trạng thái')}
                //   <KTBSDropdown
                //     name="status"
                //     toggleElement={
                //       <button
                //         className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                //         type="button"
                //       >
                //         {_.find(
                //           ChargeHelper.Data.chargeStationStatusOptions,
                //           (item) => item.value === filter?.status
                //         )?.name ?? 'Tất cả'}
                //       </button>
                //     }
                //     selectedValue={filter?.status}
                //     dropdownMenuItems={[
                //       { name: 'Tất cả', value: '' },
                //       ...ChargeHelper.Data.chargeStationStatusOptions,
                //     ]}
                //     dropdownMenuClassName="overflow-auto w-100"
                //     dropdownMenuStyle={{ maxHeight: '30vh' }}
                //     onChange={(value) => {
                //       setFilter({ ...filter, status: value, page: 0 });
                //     }}
                //     isDropdownForm={true}
                //   />
                // </div>
              )}
              {/* thiet lap cot */}
              <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                {/* bo loc */}
                <ButtonFilterDropdown
                  dropdownContent={
                    <ChargeStationCompactListFilterView
                      initialFilter={filter}
                      hideManagementObject={Utils.isValid(initialFilter?.agencyId)}
                      onCancel={() => {
                        setFilter({
                          page: 0,
                          limit: AppConfigs.defaultPageLimit,
                          status: '',
                          operatorId: '',
                          q: '',
                          agencyId: initialFilter?.agencyId ?? '',
                          landlordId: '',
                          areaId: '',
                        });
                      }}
                      onApply={(value) => {
                        setFilter({ ...filter, ...value, page: 0 });
                      }}
                    />
                  }
                  direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                />
                {/* <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  resetDefaultTableColumn={resetDefaultTableColumn}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                  saveChanges={saveChanges}
                /> */}
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={stationList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>
          </div>

          {/* pagination */}
          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter.page + 1}
              rowCount={total}
              rowsPerPage={filter.limit}
              currentItemInPageCount={stationList?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                setFilter({ ...filter, page: iNewPage - 1 });
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                setFilter({ ...filter, limit: iNewRowsPerPage, page: 0 });
              }}
              loading={false}
              color={KTPaginationColor.success}
              appendText="trạm sạc"
            />
          </div>
        </div>,
        false,
        true
      )}
    </div>
  );
}

export default ChargeStationCompactListView;
